import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const TermsOfService = () => {
  const lastUpdated = "April 1, 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#1a0527]/90 backdrop-blur-sm border-b border-[#4A0A66]/30">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#FDF2F8]/70 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
          <div className="flex items-center gap-2 text-[#0FFFE2]">
            <FileText className="w-5 h-5" />
            <span className="text-sm font-semibold">Terms of Service</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Terms of Service</h1>
          <p className="text-[#FDF2F8]/60">Last Updated: {lastUpdated}</p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          {/* Introduction */}
          <section className="bg-[#2D063E]/30 border border-[#4A0A66]/30 rounded-xl p-6">
            <p className="text-[#FDF2F8]/80 leading-relaxed">
              Welcome to UnCloned® Journal. These Terms of Service ("Terms") govern your use of 
              our application and services provided by UnCloned® Media ("we," "us," or "our"). By accessing 
              or using our service, you agree to be bound by these Terms. If you do not agree, please do 
              not use our service.
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">1. Acceptance of Terms</h2>
            <p className="text-[#FDF2F8]/80">
              By creating an account or using UnCloned® Journal, you confirm that:
            </p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4 mt-4">
              <li>You are at least 13 years of age</li>
              <li>You have the legal capacity to enter into these Terms</li>
              <li>You will use the service in compliance with all applicable laws</li>
              <li>All information you provide is accurate and complete</li>
            </ul>
          </section>

          {/* Description of Service */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">2. Description of Service</h2>
            <p className="text-[#FDF2F8]/80 mb-4">
              UnCloned® Journal is a personal journaling application that provides:
            </p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li>Digital journaling with rich text editing</li>
              <li>Voice-to-text transcription</li>
              <li>AI-powered coaching and reflections</li>
              <li>Mood tracking and analytics</li>
              <li>Streak tracking and gamification features</li>
              <li>PDF export of journal entries</li>
            </ul>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">3. User Accounts</h2>
            
            <h3 className="text-lg font-semibold text-[#0FFFE2] mb-3">Account Creation</h3>
            <p className="text-[#FDF2F8]/80 mb-4">
              You may create an account using your email address or through Google Sign-In. You are 
              responsible for maintaining the confidentiality of your account credentials and for all 
              activities that occur under your account.
            </p>

            <h3 className="text-lg font-semibold text-[#0FFFE2] mb-3">Account Security</h3>
            <p className="text-[#FDF2F8]/80">
              You agree to immediately notify us of any unauthorized use of your account. We are not 
              liable for any loss or damage arising from your failure to protect your account credentials.
            </p>
          </section>

          {/* User Content */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">4. User Content & Ownership</h2>
            
            <div className="bg-[#0FFFE2]/10 border border-[#0FFFE2]/30 rounded-lg p-4 mb-4">
              <p className="text-[#0FFFE2] font-semibold mb-2">You Own Your Content</p>
              <p className="text-[#FDF2F8]/80 text-sm">
                You retain full ownership of all journal entries, text, images, and other content you 
                create using our service. We do not claim any ownership rights to your content.
              </p>
            </div>

            <p className="text-[#FDF2F8]/80 mb-4">
              By using our service, you grant us a limited license to:
            </p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li>Store and display your content to you</li>
              <li>Process your content through AI services (for transcription and coaching)</li>
              <li>Create backups for data protection purposes</li>
            </ul>
            <p className="text-[#FDF2F8]/80 mt-4">
              We will never sell, share, or publicly display your private journal content.
            </p>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">5. Acceptable Use</h2>
            <p className="text-[#FDF2F8]/80 mb-4">You agree NOT to use our service to:</p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Upload malicious software or harmful content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt our service</li>
              <li>Create multiple accounts to abuse free features</li>
              <li>Use automated systems to access our service without permission</li>
            </ul>
          </section>

          {/* Premium Subscriptions */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">6. Premium Subscriptions & Payments</h2>
            
            <h3 className="text-lg font-semibold text-[#0FFFE2] mb-3">Subscription Plans</h3>
            <p className="text-[#FDF2F8]/80 mb-4">
              We offer premium subscription plans that provide additional features. Subscription fees 
              are billed in advance on a monthly or annual basis, depending on your selected plan.
            </p>

            <h3 className="text-lg font-semibold text-[#0FFFE2] mb-3">Automatic Renewal</h3>
            <p className="text-[#FDF2F8]/80 mb-4">
              Subscriptions automatically renew unless cancelled at least 24 hours before the end of 
              the current billing period. You can cancel your subscription at any time through your 
              account settings.
            </p>

            <div className="bg-[#FF7901]/10 border border-[#FF7901]/30 rounded-lg p-4">
              <p className="text-[#FF7901] font-semibold mb-2">Refund Policy</p>
              <p className="text-[#FDF2F8]/80 text-sm">
                All subscription payments are non-refundable. When you cancel, you will retain access 
                to premium features until the end of your current billing period.
              </p>
            </div>
          </section>

          {/* AI Features Disclaimer */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">7. AI Features Disclaimer</h2>
            <p className="text-[#FDF2F8]/80 mb-4">
              Our AI Coach and voice transcription features are powered by third-party AI services. 
              Please be aware that:
            </p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li>AI responses are generated automatically and may not always be accurate</li>
              <li>The AI Coach is not a substitute for professional mental health advice</li>
              <li>Voice transcription may contain errors</li>
              <li>AI features may be temporarily unavailable due to service disruptions</li>
            </ul>
            <p className="text-[#FDF2F8]/80 mt-4 font-medium">
              If you are experiencing a mental health crisis, please contact a qualified healthcare 
              professional or crisis helpline immediately.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">8. Limitation of Liability</h2>
            <p className="text-[#FDF2F8]/80 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li>Our service is provided "AS IS" without warranties of any kind</li>
              <li>We are not liable for any indirect, incidental, or consequential damages</li>
              <li>Our total liability shall not exceed the amount you paid us in the past 12 months</li>
              <li>We are not responsible for data loss due to circumstances beyond our control</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">9. Termination</h2>
            <p className="text-[#FDF2F8]/80 mb-4">
              We may suspend or terminate your account if you violate these Terms. You may also 
              delete your account at any time. Upon termination:
            </p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li>Your right to use the service will immediately cease</li>
              <li>We may delete your data within 30 days (unless legally required to retain it)</li>
              <li>Any unused subscription time is forfeited (no pro-rated refunds)</li>
            </ul>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">10. Changes to Terms</h2>
            <p className="text-[#FDF2F8]/80">
              We reserve the right to modify these Terms at any time. We will notify you of significant 
              changes via email or through the app. Your continued use of the service after changes 
              become effective constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">11. Governing Law</h2>
            <p className="text-[#FDF2F8]/80">
              These Terms are governed by the laws of the United States. Any disputes arising from 
              these Terms or your use of our service shall be resolved in accordance with applicable 
              U.S. law.
            </p>
          </section>

          {/* Contact Us */}
          <section className="bg-gradient-to-r from-[#df1283]/20 to-[#FF7901]/20 border border-[#df1283]/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4">12. Contact Us</h2>
            <p className="text-[#FDF2F8]/80 mb-4">
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="space-y-2">
              <p className="text-[#FDF2F8]">
                <strong>UnCloned® Media</strong>
              </p>
              <p className="text-[#FDF2F8]/80">
                Support Desk: <a href="https://www.unclonedsupport.com" target="_blank" rel="noopener noreferrer" className="text-[#0FFFE2] hover:underline">www.unclonedsupport.com</a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#4A0A66]/30 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#FDF2F8]/50 text-sm">
            © 2026 UnCloned® Media. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
